<template>
  <div class="eval-run-detail" v-loading="loading">
    <template v-if="run">
      <el-alert
        v-if="run.is_baseline"
        type="warning"
        :closable="false"
        show-icon
        title="新基线：不可直接对比 Phase 0 spike 报告数字（模型配置已不同）"
        style="margin-bottom: 12px;"
      />
      <el-alert
        v-if="run.config_source === 'local'"
        type="warning"
        :closable="false"
        show-icon
        title="本地未确认部署：本次读取的是本地 app.db 配置，可能尚未部署到生产，不建议直接作为线上基线对比"
        style="margin-bottom: 12px;"
      />

      <el-descriptions title="版本归因" border :column="3" size="small" style="margin-bottom: 24px;">
        <el-descriptions-item label="run_id">{{ run.run_id }}</el-descriptions-item>
        <el-descriptions-item label="执行时间">{{ formatTime(run.executed_at) }}</el-descriptions-item>
        <el-descriptions-item label="摄取时间">{{ formatTime(run.created_at) }}</el-descriptions-item>

        <el-descriptions-item label="粗推模型">{{ run.coarse_model }}</el-descriptions-item>
        <el-descriptions-item label="精排模型">{{ run.rerank_model }}</el-descriptions-item>
        <el-descriptions-item label="超量比">{{ fmtNum(run.overgen_ratio, 2) }}</el-descriptions-item>

        <el-descriptions-item label="粗推思考">{{ run.coarse_enable_thinking ? '开启' : '关闭' }}</el-descriptions-item>
        <el-descriptions-item label="精排思考">{{ run.rerank_enable_thinking ? '开启' : '关闭' }}</el-descriptions-item>
        <el-descriptions-item label="匹配器">
          <el-tag size="small" type="info">{{ run.matcher }}</el-tag>
        </el-descriptions-item>

        <el-descriptions-item label="persona hash">{{ run.persona_hash }}</el-descriptions-item>
        <el-descriptions-item label="粗推格式指令 hash">{{ run.coarse_format_instruction_hash }}</el-descriptions-item>
        <el-descriptions-item label="精排格式指令 hash">{{ run.rerank_format_instruction_hash }}</el-descriptions-item>

        <el-descriptions-item label="query 集版本">{{ run.query_set_version }}</el-descriptions-item>
        <el-descriptions-item label="query 集大小">{{ run.query_set_size }}</el-descriptions-item>
        <el-descriptions-item label="样本数">{{ run.sample_count }}</el-descriptions-item>

        <el-descriptions-item label="git_commit">{{ run.git_commit || '-' }}</el-descriptions-item>
        <el-descriptions-item label="harness 版本">{{ run.harness_version || '-' }}</el-descriptions-item>
        <el-descriptions-item label="数据来源">
          <el-tag :type="run.config_source === 'local' ? 'danger' : 'success'" size="small">
            {{ run.config_source === 'local' ? '本地未确认' : '已部署确认' }}
          </el-tag>
        </el-descriptions-item>

        <el-descriptions-item label="skill_id">{{ run.skill_id ?? '-' }}</el-descriptions-item>
        <el-descriptions-item label="skill_type">{{ run.skill_type }}</el-descriptions-item>
        <el-descriptions-item label="是否基线">
          <el-tag v-if="run.is_baseline" type="warning" size="small">基线</el-tag>
          <span v-else>-</span>
        </el-descriptions-item>

        <el-descriptions-item label="备注" :span="3">{{ run.notes || '-' }}</el-descriptions-item>
      </el-descriptions>

      <h4 class="section-title">样本指标（{{ run.samples?.length || 0 }} 条）</h4>
      <el-table
        v-if="run.samples && run.samples.length > 0"
        :data="run.samples"
        stripe
        size="small"
        style="width: 100%"
        class="clickable-table"
        @row-click="handleRowClick"
      >
        <el-table-column label="sample_key" prop="sample_key" width="90" />
        <el-table-column label="query" prop="query_text" show-overflow-tooltip min-width="200" />
        <el-table-column label="品类" width="110">
          <template #default="scope">
            <el-tag v-if="scope.row.query_category" size="small" type="info">{{ scope.row.query_category }}</el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="目标数" prop="target_count" width="75" />
        <el-table-column label="池匹配" prop="filter_matched_count" width="75" />
        <el-table-column label="存活率" width="85">
          <template #default="scope">{{ fmtPct(scope.row.filter_survival_vs_requested) }}</template>
        </el-table-column>
        <el-table-column label="池/目标比" width="90">
          <template #default="scope">{{ fmtNum(scope.row.filter_pool_vs_target) }}</template>
        </el-table-column>
        <el-table-column label="坍缩" width="75">
          <template #default="scope">
            <el-tag v-if="scope.row.filter_collapse_flag" type="danger" size="small">坍缩</el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="顺序惰性ρ" width="95">
          <template #default="scope">{{ fmtNum(scope.row.rerank_order_spearman) }}</template>
        </el-table-column>
        <el-table-column label="数量精确" width="85">
          <template #default="scope">
            <el-tag v-if="scope.row.rerank_count_exact" type="success" size="small">精确</el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="降级" width="75">
          <template #default="scope">
            <el-tag v-if="scope.row.rerank_is_degraded" type="warning" size="small">降级</el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
      </el-table>
      <el-empty v-else description="该 run 暂无样本数据" />
    </template>
    <el-empty v-else-if="!loading" description="run 不存在" />
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import axios from 'axios';
import { ElMessage } from 'element-plus';

const props = defineProps({
  runId: { type: String, required: true },
});
const emit = defineEmits(['select-sample']);

const run = ref(null);
const loading = ref(false);

const fmtPct = (v) => (v === null || v === undefined ? '-' : `${(v * 100).toFixed(1)}%`);
const fmtNum = (v, digits = 3) => (v === null || v === undefined ? '-' : Number(v).toFixed(digits));
const formatTime = (v) => (v ? new Date(v).toLocaleString() : '-');

const loadRun = async () => {
  if (!props.runId) return;
  loading.value = true;
  run.value = null;
  try {
    const resp = await axios.get(`/api/eval/runs/${props.runId}`);
    if (resp.data.code === 1) {
      run.value = resp.data.data;
    } else {
      ElMessage.error(resp.data.msg || '加载 run 详情失败');
    }
  } catch (err) {
    console.error('加载 run 详情失败:', err);
    if (err.response && err.response.status !== 404) {
      ElMessage.error(err.response?.data?.msg || '加载 run 详情失败');
    }
  } finally {
    loading.value = false;
  }
};

const handleRowClick = (row) => {
  // 一并把 run.pipeline_mode 带给下钻视图（pipeline_mode 是 run 级字段，drilldown 只查
  // eval_samples 拿不到），供 EvalSampleDrilldown 判定单发 vs 三段式渲染
  emit('select-sample', row.sample_key, run.value?.pipeline_mode);
};

watch(() => props.runId, loadRun);
onMounted(loadRun);
</script>

<style scoped>
.section-title {
  margin: 20px 0 12px;
  color: #303133;
}

.clickable-table :deep(.el-table__row) {
  cursor: pointer;
}
</style>
